import { lazy, Suspense, useEffect, useState } from 'react';
import Auth, { Session } from './screens/Auth';
import Chat from './screens/Chat';
import { setAuthToken, setOnUnauthorized } from './api';
import { readPendingInvite } from './invite';
import { clearVault } from './storage';
import { wipeAllPodsFromFiles } from './iko_vault';
import ToastHost, { toast } from './components/ToastHost';
import OfflineBanner from './components/OfflineBanner';
import { t } from './i18n';
import { installAutoLock, setOnLock, registerWipe, secureWipe, disarm } from './autolock';

// Demo mode: ?demo=1 renderiza un chat con mensajes mock sin auth ni
// backend, dentro del shell móvil exacto. Sirve SOLO para validar el
// layout en headless / browser sin necesidad de sesión iniciada.
const ChatDemo = lazy(() => import('./screens/ChatDemo'));
function isDemoMode(): boolean {
  try {
    const v = new URL(window.location.href).searchParams.get('demo');
    return v === '1' || v === 'home' || v === 'chat' || v === 'llamadas' || v === 'settings' || v === 'web-home' || v === 'web-chat';
  } catch { return false; }
}

export default function App() {
  // Demo mode short-circuit: bypass auth, monta ChatDemo directamente.
  if (isDemoMode()) {
    return (
      <Suspense fallback={null}>
        <ChatDemo />
      </Suspense>
    );
  }
  // baseSession: la sesión de la cuenta base (general). NUNCA cambia mientras
  // el usuario navega entre freqs — solo en logout.
  // activeSession: la sesión que se está usando ahora mismo. Igual a baseSession
  // cuando el usuario está en general; igual a una pseudo-sesión cuando está
  // dentro de una freq secreta.
  const [baseSession, setBaseSession] = useState<Session | null>(null);
  const [activeSession, setActiveSession] = useState<Session | null>(null);

  // (Aquí se detectaba una invitación entrante por URL —?t=token | ?c=code— y se
  // montaba JoinInvitation. Ese flujo solo servía para enrolar a alguien en un
  // IKO de Dominio, es decir IKOGNIT Business, retirado el 2026-08-05. A un
  // plano no se invita: quien sabe el secreto entra. La invitación de AMIGO
  // (/i#…) es otra cosa y sigue viva, más abajo, vía readPendingInvite.)

  useEffect(() => {
    setOnUnauthorized(() => {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith('ikognit_last_fetched_')) localStorage.removeItem(k);
        }
        localStorage.removeItem('ikognit_auth_mode');
      } catch (_e) { /* ignore */ }
      clearVault().catch(() => {});
      // Zero-trace móvil: al expirar la sesión, desmonta los IkoPods de Archivos
      // y borra su material del almacén seguro nativo.
      void wipeAllPodsFromFiles();
      setAuthToken(null);
      setBaseSession(null);
      setActiveSession(null);
      // Toast visible para que el usuario sepa por qué se fue a Auth en vez
      // de quedarse pensando "qué ha pasado".
      try { toast.warning(t('auth.sessionExpired'), 5000); } catch (_e) {}
    });
    return () => setOnUnauthorized(null);
  }, []);

  // Auto-lock: al pasar a segundo plano o por inactividad (15 min), borra el
  // material criptográfico de la sesión de memoria y vuelve a la pantalla de
  // desbloqueo. Cierra el flanco "robo del móvil DESBLOQUEADO con IKOGNIT abierto":
  // sin esto, wrappingKey/privateKey/DEKs viven en RAM indefinidamente.
  useEffect(() => {
    if (!baseSession) { disarm(); return; }
    const unregister = registerWipe(() => {
      const b = baseSession as unknown as Record<string, Uint8Array | undefined>;
      secureWipe(b.wrappingKey, b.privateKey, b.masterVaultKey);
      if (activeSession && activeSession !== baseSession) {
        const a = activeSession as unknown as Record<string, Uint8Array | undefined>;
        secureWipe(a.wrappingKey, a.privateKey);
      }
    });
    setOnLock(() => {
      void wipeAllPodsFromFiles();
      // NO borrar el vault al BLOQUEAR: el auto-lock solo zero-iza las claves de RAM; la sesión
      // recordada (PIN/biometría) DEBE sobrevivir para poder volver a entrar con PIN/Face ID en vez
      // de nick+password. clearVault SOLO en logout explícito u "Olvidar dispositivo" (Auth).
      setAuthToken(null);
      setBaseSession(null);
      setActiveSession(null);
      try { toast.warning(t('auth.locked'), 4000); } catch { /* */ }
    });
    // lockOnBackground:false → NO bloquear al instante en cada cambio de app (era intrusivo y en
    // móvil forzaba re-desbloqueo constante). El bloqueo por segundo plano lo gestiona main.tsx con
    // una GRACIA (solo si estuviste fuera un rato); la inactividad de 15 min cubre el resto.
    installAutoLock({ idleSeconds: 15 * 60, lockOnBackground: false });
    return () => { unregister(); setOnLock(null); };
  }, [baseSession, activeSession]);

  function handleLogout() {
    // Zero-trace móvil: cerrar sesión desmonta TODOS los IkoPods de Archivos y
    // borra su material del almacén seguro nativo.
    void wipeAllPodsFromFiles();
    setAuthToken(null);
    setBaseSession(null);
    setActiveSession(null);
  }

  function handleAuthed(s: Session) {
    setBaseSession(s);
    setActiveSession(s);
  }

  // Switch entre sesiones (general ↔ pseudo-cuenta de freq).
  // - Al entrar en una freq: activeSession pasa a la pseudo, baseSession queda
  //   en memoria para volver. El JWT activo en api.ts es el de la pseudo.
  // - Al salir: activeSession vuelve a baseSession y reactivamos su JWT.
  function handleSwitchSession(s: Session) {
    // Zero-trace móvil: cada cambio de contexto (entrar/salir de un IKO)
    // desmonta los IkoPods abiertos en el contexto anterior. Al volver a entrar
    // y abrir un Pod se vuelve a registrar. Así, al salir de un IKO, no queda
    // ninguna ubicación ni clave en Archivos. Ver [[feedback_ikognit_iko_zero_trace]].
    void wipeAllPodsFromFiles();
    setActiveSession(s);
  }

  // Hosts globales: OfflineBanner (red caída) + ToastHost (notificaciones).
  // Se montan SIEMPRE encima de la pantalla activa.
  const globalHosts = <><OfflineBanner /><ToastHost /></>;


  if (!activeSession || !baseSession) {
    // Si el invitado llegó por un enlace /i#… (guardado en localStorage por main.tsx),
    // pasamos el nick del invitador a Auth para el banner de bienvenida y para arrancar
    // en REGISTRO. La invitación de AMIGO se consume después, ya con sesión, en Chat.
    const friendInvite = readPendingInvite();
    return <>
      {globalHosts}
      <Auth invitedByNick={friendInvite?.nick} onAuthed={handleAuthed} />
    </>;
  }
  // key = ikognitId, NO token: tras el rediseño de privacidad el JWT (token) es el
  // mismo para la identidad general y para cada IKO, mientras que ikognitId cambia
  // con la identidad. Si la key fuera el token, Chat NO se remontaría al
  // entrar/salir de un IKO y el estado por-identidad (chats, grupos) quedaría
  // pegado — los chats de la general se verían dentro de cualquier IKO.
  return (
    <>
      {globalHosts}
      <Chat key={activeSession.ikognitId}
        session={activeSession}
        baseSession={baseSession}
        onSwitchSession={handleSwitchSession}
        onLogout={handleLogout}
      />
    </>
  );
}
